import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Button } from '@auctisync/ui';
import { PREVIEW_EVENT_ROW_COLUMNS, type PreviewEventRow } from '../lib/rowColumns';
import { supabase } from '../lib/supabase';

type PreviewEvent = PreviewEventRow;

interface Props {
  auctionId: string;
}

type FormState = {
  title: string;
  location: string;
  starts_at: string;
  ends_at: string;
  notes: string;
};

const EMPTY_FORM: FormState = {
  title: '',
  location: '',
  starts_at: '',
  ends_at: '',
  notes: '',
};

/**
 * Module #23 chunk 7 deferred follow-up (a): admin CRUD for scheduled preview events.
 *
 * Tenant staff can create, edit, and delete in-person preview events for an
 * auction. Bidders see the public list via `<AuctionPreviewEvents>`. RLS on
 * `auction_preview_events` already gates write to tenant_owner/tenant_staff for
 * the auction's tenant.
 */
export function AdminPreviewEvents({ auctionId }: Props) {
  const [events, setEvents] = useState<PreviewEvent[] | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const { data, error: err } = await supabase
      .from('auction_preview_events')
      .select(PREVIEW_EVENT_ROW_COLUMNS)
      .eq('auction_id', auctionId)
      .order('starts_at', { ascending: true });
    if (err) {
      setError(err.message);
      setEvents([]);
      return;
    }
    setEvents(data as PreviewEvent[]);
  }, [auctionId]);

  useEffect(() => {
    void load();
  }, [load]);

  function openCreate() {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setError(null);
    setShowForm(true);
  }

  function openEdit(ev: PreviewEvent) {
    setEditingId(ev.id);
    setForm({
      title: ev.title,
      location: ev.location ?? '',
      starts_at: isoToLocalInput(ev.starts_at),
      ends_at: ev.ends_at ? isoToLocalInput(ev.ends_at) : '',
      notes: ev.notes ?? '',
    });
    setError(null);
    setShowForm(true);
  }

  function closeForm() {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
    setError(null);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.title.trim() || !form.starts_at) return;

    const startsIso = localInputToIso(form.starts_at);
    const endsIso = form.ends_at ? localInputToIso(form.ends_at) : null;
    if (endsIso && new Date(endsIso) <= new Date(startsIso)) {
      setError('End time must be after start time.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const payload = {
        auction_id: auctionId,
        title: form.title.trim(),
        location: form.location.trim() || null,
        starts_at: startsIso,
        ends_at: endsIso,
        notes: form.notes.trim() || null,
      };
      if (editingId) {
        const { error: err } = await supabase
          .from('auction_preview_events')
          .update(payload)
          .eq('id', editingId);
        if (err) throw err;
      } else {
        const { error: err } = await supabase.from('auction_preview_events').insert(payload);
        if (err) throw err;
      }
      closeForm();
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: string, title: string) {
    if (!window.confirm(`Delete preview event "${title}"? This cannot be undone.`)) return;
    setSaving(true);
    setError(null);
    try {
      const { error: err } = await supabase.from('auction_preview_events').delete().eq('id', id);
      if (err) throw err;
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <section style={{ marginTop: '2.5rem' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          marginBottom: '1rem',
          flexWrap: 'wrap',
          gap: '0.5rem',
        }}
      >
        <div>
          <h2
            style={{
              fontSize: '1.25rem',
              fontWeight: 600,
              color: 'var(--text)',
              marginBottom: '0.2rem',
            }}
          >
            Preview events
          </h2>
          <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', margin: 0 }}>
            Schedule in-person preview times so bidders can visit before bidding closes.
          </p>
        </div>
        <Button type="button" variant="outline" onClick={openCreate} disabled={showForm}>
          + Add preview event
        </Button>
      </div>

      {error ? <AlertBox>{error}</AlertBox> : null}

      {showForm ? (
        <form
          onSubmit={(e) => {
            void handleSubmit(e);
          }}
          style={{
            border: '1px solid var(--border)',
            borderRadius: '0.5rem',
            padding: '1rem 1.25rem',
            background: 'var(--surface-elev)',
            marginBottom: '1rem',
            display: 'grid',
            gap: '0.75rem',
          }}
        >
          <Field label="Title" required>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              maxLength={200}
              required
              style={inputStyle}
              placeholder="Saturday Open House"
            />
          </Field>
          <div
            style={{
              display: 'grid',
              gap: '0.75rem',
              gridTemplateColumns: 'repeat(auto-fit, minmax(14rem, 1fr))',
            }}
          >
            <Field label="Starts at" required>
              <input
                type="datetime-local"
                value={form.starts_at}
                onChange={(e) => setForm({ ...form, starts_at: e.target.value })}
                required
                style={inputStyle}
              />
            </Field>
            <Field label="Ends at">
              <input
                type="datetime-local"
                value={form.ends_at}
                onChange={(e) => setForm({ ...form, ends_at: e.target.value })}
                style={inputStyle}
              />
            </Field>
          </div>
          <Field label="Location">
            <input
              type="text"
              value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
              maxLength={500}
              style={inputStyle}
              placeholder="123 Main St, St. Louis MO"
            />
          </Field>
          <Field label="Notes">
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              maxLength={2000}
              rows={3}
              style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
              placeholder="Parking on the south side. Ring buzzer for entry."
            />
          </Field>
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
            <Button type="button" variant="outline" onClick={closeForm} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving || !form.title.trim() || !form.starts_at}>
              {saving ? 'Saving…' : editingId ? 'Update event' : 'Create event'}
            </Button>
          </div>
        </form>
      ) : null}

      {events === null ? (
        <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>Loading…</p>
      ) : events.length === 0 ? (
        <div
          style={{
            padding: '2rem 1rem',
            textAlign: 'center',
            color: 'var(--text-muted)',
            border: '1px dashed var(--border)',
            borderRadius: '0.5rem',
            fontSize: '0.875rem',
          }}
        >
          No preview events scheduled yet.
        </div>
      ) : (
        <ul
          style={{
            display: 'grid',
            gap: '0.5rem',
            listStyle: 'none',
            padding: 0,
            margin: 0,
          }}
        >
          {events.map((ev) => {
            const past = new Date(ev.starts_at).getTime() < Date.now();
            return (
              <li
                key={ev.id}
                style={{
                  border: '1px solid var(--border)',
                  borderRadius: '0.5rem',
                  padding: '0.85rem 1rem',
                  background: 'var(--surface-elev)',
                  display: 'flex',
                  gap: '1rem',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  opacity: past ? 0.7 : 1,
                }}
              >
                <div style={{ minWidth: 0, flex: '1 1 18rem' }}>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'baseline' }}>
                    <strong style={{ color: 'var(--text)' }}>{ev.title}</strong>
                    {past ? (
                      <span
                        style={{
                          fontSize: '0.7rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          color: 'var(--text-muted)',
                        }}
                      >
                        past
                      </span>
                    ) : null}
                  </div>
                  <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                    {formatEventTime(ev.starts_at, ev.ends_at)}
                  </div>
                  {ev.location ? (
                    <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                      📍 {ev.location}
                    </div>
                  ) : null}
                  {ev.notes ? (
                    <div
                      style={{
                        fontSize: '0.85rem',
                        color: 'var(--text)',
                        marginTop: '0.25rem',
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {ev.notes}
                    </div>
                  ) : null}
                </div>
                <div style={{ display: 'flex', gap: '0.35rem', flexShrink: 0 }}>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => openEdit(ev)}
                    disabled={saving}
                  >
                    Edit
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      void handleDelete(ev.id, ev.title);
                    }}
                    disabled={saving}
                  >
                    Delete
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <label style={{ display: 'grid', gap: '0.35rem' }}>
      <span
        style={{
          fontSize: '0.8rem',
          fontWeight: 500,
          color: 'var(--text)',
        }}
      >
        {label}
        {required ? <span style={{ color: 'var(--semantic-error, #b91c1c)' }}> *</span> : null}
      </span>
      {children}
    </label>
  );
}

function AlertBox({ children }: { children: ReactNode }) {
  return (
    <div
      role="alert"
      style={{
        padding: '0.75rem 1rem',
        borderRadius: '0.375rem',
        background: 'var(--surface-danger, #fef2f2)',
        color: 'var(--text-danger, #991b1b)',
        border: '1px solid var(--border-danger, #fecaca)',
        marginBottom: '1rem',
        fontSize: '0.875rem',
      }}
    >
      {children}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.5rem 0.75rem',
  border: '1px solid var(--border)',
  borderRadius: '0.375rem',
  background: 'var(--surface)',
  color: 'var(--text)',
  fontSize: '0.875rem',
  fontFamily: 'inherit',
};

/**
 * Format an ISO timestamp pair like AuctionPreviewEvents does, so admin + public
 * see the same string for the same event.
 */
function formatEventTime(startsAt: string, endsAt: string | null): string {
  const start = new Date(startsAt);
  const startStr = start.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
  if (!endsAt) return startStr;
  const end = new Date(endsAt);
  const sameDay = start.toDateString() === end.toDateString();
  const endStr = sameDay
    ? end.toLocaleString(undefined, { hour: 'numeric', minute: '2-digit' })
    : end.toLocaleString(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
  return `${startStr} → ${endStr}`;
}

/**
 * <input type="datetime-local"> reads/writes a string in the LOCAL timezone
 * with format `YYYY-MM-DDTHH:mm`. The DB stores UTC. Convert both directions
 * via the browser's Date object (which interprets the local string in the
 * local zone).
 */
function isoToLocalInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function localInputToIso(local: string): string {
  return new Date(local).toISOString();
}
